// client/src/routes/app/contracts/$contractId/route.tsx — SCR-CON-003, the tabs layout ·
// CON-005 (04 §8.4, §5.10).
//
// THE TAB SET IS 04 §8.4's, ALL SIX. CON-005 shipped five: „C168 (M4…)" was a promise the app
// could not keep, so the unbuilt milestone got no tab and its honest fact — `c168Registered`, a
// column CON-001 already serialises — was a chip in the header instead (D-388). CON-006 lands
// the event list FISC-002 feeds, so the sixth tab is here, between „Documente" and the end, and
// the chip beside the status is now its link — which is the reversal D-388 wrote for itself.
// The tab is hidden from a reader without `Fiscal.Read`; the route itself renders SCR-SYS-403
// for the URL that was typed anyway (§9.2.2).
//
// Three of the six tabs were SEAMS (`features/contracts/panels.tsx`): the periods RENT-001
// generates, CON-003's garanție machine and CON-004's generated PDFs, all filled by CON-005's
// own phase 2. They are mounted at their real routes under their real tabs with their real i18n
// keys, so that phase replaced one body each and no route, key or caller moved.
//
// The header carries the identity, the three state chips of §8.4's row spec — the
// `StatusBadge`, the derived 🟠 expiry chip and the C168 chip — and every action the status
// machine allows, so all six tabs have them without repeating them six times. The obligations
// read behind the C168 chip is organization-wide and shared with SCR-CON-001 under the generated
// key, so opening a contract from the list costs no second request.

import { Outlet, createFileRoute, linkOptions } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { DetailHeader } from '@/components/layout/DetailHeader';
import { ProfileTabs } from '@/components/layout/ProfileTabs';
import { RequirePermission } from '@/components/layout/route-guards';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { fiscalVisible } from '@/config/fiscal';
import { C168Badge } from '@/features/contracts/C168Badge';
import { ContractActions } from '@/features/contracts/ContractActions';
import { ExpiryChip } from '@/features/contracts/ExpiryChip';
import { c168RowsOf, contractChipState } from '@/features/contracts/c168';
import { useContract } from '@/features/contracts/queries';
import { useContractFiscalObligations } from '@/features/fiscal/queries';
import { todayInBucharest } from '@/features/payments/today';
import { wireNumber } from '@/lib/wire';
import { useCan } from '@/session';

export const Route = createFileRoute('/app/contracts/$contractId')({
  component: ContractDetailLayout,
});

function ContractDetailLayout() {
  const { contractId } = Route.useParams();
  const { t } = useTranslation();
  const contract = useContract(contractId);
  const canReadFiscal = useCan('Fiscal.Read');
  // The C168 chip and tab are withdrawn from view with the fiscal module (D-2780), and the read
  // behind them is not made.
  const fiscal = fiscalVisible();
  // One organization-wide read, shared with SCR-CON-001 under the generated key. Skipped
  // entirely for a reader without `Fiscal.Read`, whose chip falls back to D-385's placeholder.
  const obligations = useContractFiscalObligations(canReadFiscal && fiscal);

  if (contract.isPending) return <LoadingState rows={4} />;
  if (contract.isError) {
    return <ErrorState error={contract.error} onRetry={() => void contract.refetch()} />;
  }

  const detail = contract.data;

  return (
    <RequirePermission permission="Leasing.Read">
      <DetailHeader
        backTo="/app/contracts"
        backLabel={t('contracts.list.title')}
        glyph="document-text"
        title={detail.contractNumber}
        subtitle={
          detail.unitName == null
            ? detail.propertyName
            : t('contracts.detail.propertyAndUnit', {
                property: detail.propertyName,
                unit: detail.unitName,
              })
        }
        chips={
          <>
            <StatusBadge kind="ContractStatus" value={detail.status} />
            <ExpiryChip isExpiringSoon={detail.isExpiringSoon} daysUntilEnd={detail.daysUntilEnd} />
            {fiscal ? (
              <C168Badge
                state={contractChipState(
                  c168RowsOf(obligations.data, detail.id),
                  todayInBucharest(),
                  {
                    registered: detail.c168Registered,
                    registeredDate: detail.c168RegisteredDate,
                    registrationNumber: detail.c168RegistrationNumber,
                  },
                )}
                contractId={canReadFiscal ? contractId : undefined}
              />
            ) : null}
            {wireNumber(detail.amendmentCount) > 0 ? (
              <span className="chip chip-info">
                {t('contracts.detail.amendmentCount', {
                  count: wireNumber(detail.amendmentCount),
                })}
              </span>
            ) : null}
          </>
        }
        actions={<ContractActions contract={detail} />}
      />

      {/* D-2905: the shared section bar the property and tenant pages use (D-2900), in three
          groups — the terms; the money over time (rent schedule, amendments, deposit); the
          paperwork (documents and, where fiscal is shown, C168). The order is §8.4's. */}
      <ProfileTabs
        label={t('contracts.detail.tabsLabel')}
        groups={[
          [
            {
              label: t('contracts.detail.tabs.terms'),
              icon: 'document-text',
              primary: true,
              link: linkOptions({ to: '/app/contracts/$contractId', params: { contractId } }),
              exact: true,
            },
          ],
          [
            {
              label: t('contracts.detail.tabs.schedule'),
              icon: 'calendar',
              primary: true,
              link: linkOptions({
                to: '/app/contracts/$contractId/schedule',
                params: { contractId },
              }),
            },
            {
              label: t('contracts.detail.tabs.amendments'),
              icon: 'clipboard-check',
              primary: true,
              link: linkOptions({
                to: '/app/contracts/$contractId/amendments',
                params: { contractId },
              }),
            },
            {
              label: t('contracts.detail.tabs.deposit'),
              icon: 'shield-check',
              primary: true,
              link: linkOptions({
                to: '/app/contracts/$contractId/deposit',
                params: { contractId },
              }),
            },
          ],
          [
            {
              label: t('contracts.detail.tabs.documents'),
              icon: 'folder',
              primary: true,
              link: linkOptions({
                to: '/app/contracts/$contractId/documents',
                params: { contractId },
              }),
            },
            canReadFiscal &&
              fiscal && {
                label: t('contracts.detail.tabs.c168'),
                icon: 'scale',
                link: linkOptions({ to: '/app/contracts/$contractId/c168', params: { contractId } }),
              },
          ],
        ]}
      />

      <Outlet />
    </RequirePermission>
  );
}
